import * as React from "react";

import {
  assignTracks,
  clipThresholds,
  computeGaps,
  type Gap,
  placeClips,
  sortItems,
  type TrackLayout,
  unionIntervals,
} from "./layout";
import type { TimelineTrackItem } from "./TimelineTrackItem";
import { type AxisTick, computeTicks, type TickFormatter } from "./timeTicks";
import { fractionOf, type TimeWindow } from "./window";

/** Gaps narrower than this many pixels get no "Idle …" label. */
const MIN_GAP_LABEL_PX = 140;

export interface TrackLayoutResult<T> {
  /** `null` until the container has been measured. */
  layout: TrackLayout<T> | null;
  ticks: AxisTick[];
  /** Interior gaps wide enough to label. */
  labelledGaps: Gap[];
}

/**
 * The layout pipeline as memoized calculations, one per input that can
 * change: items → sorted → tracks (per data), → clips (per window, selection
 * and width), ticks (per window and width), gaps (per clips). The measured
 * width enters only through the pixel thresholds.
 */
export function useTrackLayout<T>(
  items: readonly TimelineTrackItem<T>[],
  timeWindow: TimeWindow,
  selectedId: string | null,
  width: number | null,
  tickFormatter: TickFormatter,
): TrackLayoutResult<T> {
  const sorted = React.useMemo(() => sortItems(items), [items]);
  const { trackById } = React.useMemo(() => assignTracks(sorted), [sorted]);
  const thresholds = React.useMemo(
    () => (width === null ? null : clipThresholds(width)),
    [width],
  );
  const layout = React.useMemo(
    () =>
      thresholds === null
        ? null
        : placeClips(sorted, trackById, timeWindow, selectedId, thresholds),
    [sorted, trackById, timeWindow, selectedId, thresholds],
  );
  const ticks = React.useMemo(
    () =>
      width === null ? [] : computeTicks(timeWindow, width, tickFormatter),
    [timeWindow, width, tickFormatter],
  );
  const labelledGaps = React.useMemo(() => {
    if (!layout || width === null) {
      return [];
    }
    const minFraction = MIN_GAP_LABEL_PX / width;
    return computeGaps(unionIntervals(layout.visible), timeWindow).filter(
      (gap) =>
        !gap.edge &&
        fractionOf(timeWindow, gap.end) - fractionOf(timeWindow, gap.start) >=
          minFraction,
    );
  }, [layout, timeWindow, width]);

  return { layout, ticks, labelledGaps };
}
